import { firstChildNS, wAttr, W_NS } from "./xmlUtils";

export interface RunMeta {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  superscript: boolean;
  subscript: boolean;
  fontSizePt: number | null;
  fontFamily: string | null;
  allCaps: boolean;
}

export interface ParagraphRunsMeta {
  /** Index among all w:p in document order */
  index: number;
  runs: RunMeta[];
}

function readBooleanElement(el: Element | null): boolean {
  if (!el) return false;
  const val = wAttr(el, "val");
  return val !== "false" && val !== "0";
}

function readUnderline(el: Element | null): boolean {
  if (!el) return false;
  const val = wAttr(el, "val");
  return val !== undefined && val !== "false" && val !== "0" && val !== "none";
}

function readRunMeta(rEl: Element): RunMeta | null {
  const textParts: string[] = [];
  const texts = rEl.getElementsByTagNameNS(W_NS, "t");
  for (let i = 0; i < texts.length; i++) {
    textParts.push(texts[i].textContent ?? "");
  }
  const text = textParts.join("");
  if (!text && !textParts.length) {
    return null;
  }

  const rPr = firstChildNS(rEl, "rPr");
  const szEl = rPr ? firstChildNS(rPr, "sz") : null;
  const szVal = szEl ? wAttr(szEl, "val") : null;
  const fontSizePt =
    szVal && /^\d+$/.test(szVal) ? parseInt(szVal, 10) / 2 : null;

  const rFonts = rPr ? firstChildNS(rPr, "rFonts") : null;
  const fontFamily = rFonts
    ? wAttr(rFonts, "ascii") ?? wAttr(rFonts, "hAnsi")
    : null;

  const vertAlign = rPr ? firstChildNS(rPr, "vertAlign") : null;
  const vertVal = vertAlign ? wAttr(vertAlign, "val") : null;

  return {
    text,
    bold: readBooleanElement(rPr ? firstChildNS(rPr, "b") : null),
    italic: readBooleanElement(rPr ? firstChildNS(rPr, "i") : null),
    underline: readUnderline(rPr ? firstChildNS(rPr, "u") : null),
    strike: readBooleanElement(rPr ? firstChildNS(rPr, "strike") : null),
    superscript: vertVal === "superscript",
    subscript: vertVal === "subscript",
    fontSizePt,
    fontFamily,
    allCaps: readBooleanElement(rPr ? firstChildNS(rPr, "caps") : null),
  };
}

export function extractRunsFromParagraph(pEl: Element, index: number): ParagraphRunsMeta {
  const runs: RunMeta[] = [];
  const rElements = pEl.getElementsByTagNameNS(W_NS, "r");
  for (let i = 0; i < rElements.length; i++) {
    const meta = readRunMeta(rElements[i]);
    if (meta) runs.push(meta);
  }
  return { index, runs };
}

export function runsHaveFormatting(runs: RunMeta[]): boolean {
  return runs.some(
    (r) =>
      r.bold ||
      r.italic ||
      r.underline ||
      r.strike ||
      r.superscript ||
      r.subscript ||
      isMeaningfulFontSize(r.fontSizePt) ||
      isMeaningfulFontFamily(r.fontFamily) ||
      r.allCaps,
  );
}

const DEFAULT_BODY_FONT_PT = 13;
const NOISE_FONTS = new Set([
  "calibri",
  "cambria",
  "arial",
  "helvetica",
  "Segoe UI".toLowerCase(),
]);

export function isMeaningfulFontSize(pt: number | null): boolean {
  if (pt == null) return false;
  return Math.abs(pt - DEFAULT_BODY_FONT_PT) > 0.1;
}

export function isMeaningfulFontFamily(font: string | null): boolean {
  if (!font) return false;
  const normalized = font.replace(/['"]/g, "").trim();
  if (!normalized) return false;
  if (NOISE_FONTS.has(normalized.toLowerCase())) return false;
  return true;
}

export function paragraphPlainTextFromRuns(runs: RunMeta[]): string {
  return runs.map((r) => r.text).join("");
}
